import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Copy, Plus, Search, MousePointerClick, ShoppingCart, Loader2, Link2, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = window.location.origin;
const SITE_URL = 'https://sisterdiamonds.com';

const AdminAffiliatesTab = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newAffiliate, setNewAffiliate] = useState({ full_name: '', email: '', phone: '', company: '', custom_code: '' });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    // Auto-scan for unattributed sales silently then load
    axios.post(`${API_URL}/api/admin/affiliates/run-attribution`, {}, { headers: { Authorization: `Bearer ${getToken()}` } })
      .finally(() => loadAffiliates());
  }, []);

  const loadAffiliates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/affiliates`, { headers: { Authorization: `Bearer ${getToken()}` } });
      // Load purchase details for each affiliate
      const withDetails = await Promise.all(res.data.map(async (aff) => {
        try {
          const detail = await axios.get(`${API_URL}/api/admin/affiliates/${aff.id}/detail`, { headers: { Authorization: `Bearer ${getToken()}` } });
          return { ...aff, purchases: detail.data.conversions || [], clicks_detail: detail.data.clicks || [] };
        } catch { return { ...aff, purchases: [], clicks_detail: [] }; }
      }));
      setAffiliates(withDetails);
    } catch { toast.error('Failed to load affiliates'); }
    finally { setLoading(false); }
  };

  const addAffiliate = async () => {
    if (!newAffiliate.full_name || !newAffiliate.email) { toast.error('Name and email are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/affiliates/create`, { ...newAffiliate, commission_type: 'percentage_10' }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Affiliate added!');
      setShowAddDialog(false);
      setNewAffiliate({ full_name: '', email: '', phone: '', company: '', custom_code: '' });
      loadAffiliates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add affiliate'); }
    finally { setSaving(false); }
  };

  const deleteAffiliate = async (id) => {
    if (!window.confirm('Remove this affiliate?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/affiliates/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Affiliate removed');
      loadAffiliates();
    } catch { toast.error('Delete failed'); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  const filtered = affiliates.filter(a =>
    !search || a.full_name?.toLowerCase().includes(search.toLowerCase()) || a.email?.toLowerCase().includes(search.toLowerCase()) || a.affiliate_code?.toLowerCase().includes(search.toLowerCase())
  );

  const totalClicks = affiliates.reduce((s, a) => s + (a.total_clicks || 0), 0);
  const totalPurchases = affiliates.reduce((s, a) => s + (a.total_conversions || 0), 0);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-affiliates-tab">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Users, label: 'Affiliates', value: affiliates.length, color: '#1A3C34' },
          { icon: MousePointerClick, label: 'Total Clicks', value: totalClicks, color: '#C06C54' },
          { icon: ShoppingCart, label: 'Purchases', value: totalPurchases, color: '#059669' },
        ].map((s, i) => (
          <Card key={i} className="card-base">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${s.color}10` }}>
                <s.icon className="h-5 w-5" style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="text-lg font-bold text-forest">{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search by name, email, or code..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="affiliate-search" />
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-forest hover:bg-forest/90 text-sand" data-testid="add-affiliate-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Affiliate
        </Button>
      </div>

      {/* Affiliate Cards */}
      {filtered.length === 0 ? (
        <Card className="card-base">
          <CardContent className="p-8 text-center">
            <p className="text-sm text-gray-400">No affiliates yet. Click "Add Affiliate" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map(aff => {
            const refLink = `${SITE_URL}/retreats?ref=${aff.affiliate_code}`;
            return (
              <Card key={aff.id} className="card-base overflow-hidden" data-testid={`affiliate-card-${aff.id}`}>
                {/* Header */}
                <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 border-b border-gray-100">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-forest text-base">{aff.full_name}</p>
                      <Badge className="text-[10px]" style={{
                        backgroundColor: aff.status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                        color: aff.status === 'active' ? '#059669' : '#d97706'
                      }}>{aff.status}</Badge>
                    </div>
                    <p className="text-xs text-gray-500">{aff.email}</p>
                  </div>

                  {/* Link + Copy */}
                  <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-2 rounded-lg">
                    <Link2 className="h-3.5 w-3.5 text-terracotta shrink-0" />
                    <code className="text-[11px] text-gray-600 truncate max-w-[280px]">{refLink}</code>
                    <button onClick={() => copy(refLink)} className="p-1 hover:bg-gray-200 rounded" data-testid={`copy-link-${aff.id}`}>
                      <Copy className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  </div>

                  {/* Quick Stats */}
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1 text-gray-500"><MousePointerClick className="h-3.5 w-3.5" /> {aff.total_clicks || 0} clicks</span>
                    <span className="flex items-center gap-1 font-semibold text-green-700"><ShoppingCart className="h-3.5 w-3.5" /> {aff.total_conversions || 0} purchases</span>
                  </div>

                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => deleteAffiliate(aff.id)} data-testid={`delete-${aff.id}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>

                {/* Purchases List - Always Visible */}
                <div className="bg-gray-50/50 px-4 py-3">
                  <p className="text-xs font-semibold text-forest mb-2">
                    Purchases through {aff.full_name}'s link ({aff.purchases?.length || 0})
                  </p>
                  {aff.purchases?.length > 0 ? (
                    <div className="space-y-2">
                      {aff.purchases.map((p, i) => (
                        <div key={i} className="flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-gray-100">
                          <div>
                            <p className="text-sm font-medium text-forest">{p.customer_email}</p>
                            <p className="text-xs text-gray-400">{p.product_name || 'Retreat Booking'}</p>
                          </div>
                          <p className="text-[11px] text-gray-400">{new Date(p.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 text-center py-3">No purchases yet through this link.</p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Affiliate Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-forest">
              <Plus className="h-5 w-5" /> Add Affiliate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-gray-600">Full Name *</Label>
              <Input value={newAffiliate.full_name} onChange={e => setNewAffiliate(f => ({...f, full_name: e.target.value}))} placeholder="Antonio Garcia" data-testid="new-aff-name" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Email *</Label>
              <Input type="email" value={newAffiliate.email} onChange={e => setNewAffiliate(f => ({...f, email: e.target.value}))} placeholder="antonio@email.com" data-testid="new-aff-email" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Phone</Label>
              <Input value={newAffiliate.phone} onChange={e => setNewAffiliate(f => ({...f, phone: e.target.value}))} placeholder="(555) 000-0000" data-testid="new-aff-phone" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Company / Brand</Label>
              <Input value={newAffiliate.company} onChange={e => setNewAffiliate(f => ({...f, company: e.target.value}))} placeholder="Company name" data-testid="new-aff-company" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Custom Code (optional — auto-generated if blank)</Label>
              <Input value={newAffiliate.custom_code} onChange={e => setNewAffiliate(f => ({...f, custom_code: e.target.value.toUpperCase()}))} placeholder="e.g. ANTONIO" data-testid="new-aff-code" />
            </div>
            <Button onClick={addAffiliate} disabled={saving} className="w-full bg-forest hover:bg-forest/90 text-sand" data-testid="save-affiliate-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Affiliate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAffiliatesTab;
